/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { PreferenzeUtenteDTO } from '../schemas/preferenzeUtenteDTO.ts';
import type { PreferenzeUtenteRequest } from '../schemas/preferenzeUtenteRequest.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Legge le preferenze di comunicazione dell'utente loggato
 * @summary Legge le preferenze di comunicazione dell'utente loggato
 */
export const getPreferenzeUtente = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<PreferenzeUtenteDTO>(
    { url: `/framework-cfg-utenti/preferenze-utente`, method: 'get', signal },
    options,
  );
};

export const getGetPreferenzeUtenteQueryKey = () =>
  [`/framework-cfg-utenti/preferenze-utente`] as const;

export const getGetPreferenzeUtenteQueryOptions = <
  TData = Awaited<ReturnType<typeof getPreferenzeUtente>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getPreferenzeUtente>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getPreferenzeUtente>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPreferenzeUtenteQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPreferenzeUtente>>
  > = ({ signal }) => getPreferenzeUtente(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetPreferenzeUtenteQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPreferenzeUtente>>
>;
export type GetPreferenzeUtenteQueryError = ErrorType<unknown>;

/**
 * @summary Legge le preferenze di comunicazione dell'utente loggato
 */
export const useGetPreferenzeUtente = <
  TData = Awaited<ReturnType<typeof getPreferenzeUtente>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getPreferenzeUtente>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetPreferenzeUtenteQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Salva le preferenze di comunicazione dell'utente loggato
 * @summary Salva le preferenze di comunicazione dell'utente loggato
 */
export const putPreferenzeUtente = (
  preferenzeUtenteRequest: PreferenzeUtenteRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<PreferenzeUtenteDTO>(
    {
      url: `/framework-cfg-utenti/preferenze-utente`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: preferenzeUtenteRequest,
    },
    options,
  );
};

export const getPutPreferenzeUtenteMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putPreferenzeUtente>>,
    TError,
    { data: PreferenzeUtenteRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putPreferenzeUtente>>,
  TError,
  { data: PreferenzeUtenteRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putPreferenzeUtente>>,
    { data: PreferenzeUtenteRequest }
  > = props => {
    const { data } = props ?? {};

    return putPreferenzeUtente(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutPreferenzeUtenteMutationResult = NonNullable<
  Awaited<ReturnType<typeof putPreferenzeUtente>>
>;
export type PutPreferenzeUtenteMutationBody = PreferenzeUtenteRequest;
export type PutPreferenzeUtenteMutationError = ErrorType<unknown>;

/**
 * @summary Salva le preferenze di comunicazione dell'utente loggato
 */
export const usePutPreferenzeUtente = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putPreferenzeUtente>>,
    TError,
    { data: PreferenzeUtenteRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutPreferenzeUtenteMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Legge le preferenze di comunicazione per idUtente (B2B)
 * @summary Legge le preferenze di comunicazione per idUtente (B2B)
 */
export const getPreferenzeUtenteB2BIdUtente = (
  idUtente: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<PreferenzeUtenteDTO>(
    {
      url: `/framework-cfg-utenti/preferenze-utente/b2b/${idUtente}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetPreferenzeUtenteB2BIdUtenteQueryKey = (idUtente: string) =>
  [`/framework-cfg-utenti/preferenze-utente/b2b/${idUtente}`] as const;

export const getGetPreferenzeUtenteB2BIdUtenteQueryOptions = <
  TData = Awaited<ReturnType<typeof getPreferenzeUtenteB2BIdUtente>>,
  TError = ErrorType<unknown>,
>(
  idUtente: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPreferenzeUtenteB2BIdUtente>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getPreferenzeUtenteB2BIdUtente>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetPreferenzeUtenteB2BIdUtenteQueryKey(idUtente);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPreferenzeUtenteB2BIdUtente>>
  > = ({ signal }) =>
    getPreferenzeUtenteB2BIdUtente(idUtente, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idUtente, ...queryOptions };
};

export type GetPreferenzeUtenteB2BIdUtenteQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPreferenzeUtenteB2BIdUtente>>
>;
export type GetPreferenzeUtenteB2BIdUtenteQueryError = ErrorType<unknown>;

/**
 * @summary Legge le preferenze di comunicazione per idUtente (B2B)
 */
export const useGetPreferenzeUtenteB2BIdUtente = <
  TData = Awaited<ReturnType<typeof getPreferenzeUtenteB2BIdUtente>>,
  TError = ErrorType<unknown>,
>(
  idUtente: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPreferenzeUtenteB2BIdUtente>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetPreferenzeUtenteB2BIdUtenteQueryOptions(
    idUtente,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
